import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { getOssBridge, isLikelyWebView, isNativeOssApp, isStandaloneDisplay } from "../../lib/oss/bridge";

export const OSS_INSTALL_DISMISSED_KEY = "oss-install-dismissed";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function isIosSafari(ua) {
  return /(iPhone|iPad|iPod)/.test(ua) && /Safari\//.test(ua) && !/(CriOS|FxiOS|EdgiOS)/.test(ua);
}

export default function OssInstallPrompt({ enabled = true }) {
  const [deferred, setDeferred] = useState(null);
  const [iosHint, setIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (isNativeOssApp(ua) || getOssBridge() || isLikelyWebView(ua) || isStandaloneDisplay()) return undefined;
    const dismissed = parseInt(window.localStorage.getItem(OSS_INSTALL_DISMISSED_KEY) || "0", 10);
    if (dismissed && Date.now() - dismissed < SNOOZE_MS) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (isIosSafari(ua)) {
      setIosHint(true);
      setVisible(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!enabled || !visible) return null;

  const dismiss = () => {
    window.localStorage.setItem(OSS_INSTALL_DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Installa OSS"
      className="fixed inset-x-3 z-50 mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 shadow-xl"
      style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}
    >
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold">Tieni l'area OSS sul tablet</p>
        {iosHint ? (
          <p className="mt-1 text-slate-500 dark:text-slate-400">In Safari tocca Condividi, poi Aggiungi a Home.</p>
        ) : (
          <p className="mt-1 text-slate-500 dark:text-slate-400">Installala come app: si apre a tutto schermo, senza il menu del browser.</p>
        )}
        {!iosHint && deferred ? (
          <button
            type="button"
            className="mt-2 inline-flex h-11 items-center gap-2 rounded-xl bg-sky-700 px-4 text-sm font-semibold text-white"
            onClick={async () => {
              deferred.prompt();
              await deferred.userChoice;
              setDeferred(null);
              setVisible(false);
            }}
          >
            <Download className="h-4 w-4" /> Installa
          </button>
        ) : null}
        {iosHint ? <Share className="mt-2 h-4 w-4 text-slate-500 dark:text-slate-400" aria-hidden /> : null}
      </div>
      <button type="button" className="flex h-11 w-11 items-center justify-center rounded-lg text-slate-500 dark:text-slate-400" aria-label="Non ora" onClick={dismiss}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
